import { useTranslation } from "react-i18next";
import { ArrowRight, Clock3, X } from "lucide-react";

import { CommandItem } from "@/components/ui/command";
import { cn } from "@/utils/cn";

import type { ISearchEntry } from "../types";

interface SearchResultRowProps {
  entry: ISearchEntry;
  valuePrefix?: string;
  isRecent?: boolean;
  onSelect: (entry: ISearchEntry) => void;
  onRemove?: (entry: ISearchEntry) => void;
}

export const SearchResultRow = ({
  entry,
  valuePrefix = "",
  isRecent = false,
  onSelect,
  onRemove,
}: SearchResultRowProps) => {
  const { t } = useTranslation(["nav", "common"]);

  return (
    <CommandItem
      value={`${valuePrefix}${entry.label} ${entry.path}`}
      keywords={entry.keywords}
      onSelect={() => onSelect(entry)}
      className="group gap-3 py-2 data-[selected=true]:bg-primary-muted"
    >
      {/* Icon — colored bg for modules, muted for sub-pages */}
      <span
        className={cn(
          "relative flex shrink-0 items-center justify-center rounded-sxs",
          entry.isModule
            ? "size-8 bg-primary-muted text-primary"
            : "size-7 bg-muted text-muted-foreground",
        )}
      >
        <span className="flex size-5 items-center justify-center">
          {entry.icon}
        </span>
        {isRecent && (
          <span className="absolute -bottom-0.5 -end-0.5 flex size-3 items-center justify-center rounded-full bg-surface ring-1 ring-border">
            <Clock3 className="size-2 text-muted-foreground" />
          </span>
        )}
      </span>

      {/* Breadcrumb + label */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[10px] font-medium leading-none text-muted-foreground/70">
          {entry.space === "myspace"
            ? t("nav:tabs.my_space")
            : t("nav:tabs.workspace")}
          {!entry.isModule && <> › {entry.module}</>}
          {entry.parentLabel && <> › {entry.parentLabel}</>}
        </span>
        <span
          className={cn(
            "truncate text-sm leading-snug",
            entry.isModule ? "font-medium" : "font-normal text-foreground",
          )}
        >
          {entry.label}
        </span>
      </div>

      {/* Arrow — slides in on highlight; suppressed for recents (remove btn takes its place) */}
      {!isRecent && (
        <ArrowRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-all duration-150 group-data-[selected=true]:translate-x-0.5 group-data-[selected=true]:opacity-100 rtl-flip" />
      )}

      {/* Per-row remove for recents */}
      {isRecent && onRemove && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(entry);
          }}
          className="hidden size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground group-hover:flex group-data-[selected=true]:flex"
          aria-label={`Remove ${entry.label} from recents`}
        >
          <X className="size-3" />
        </button>
      )}
    </CommandItem>
  );
};
